import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  FiClock,
  FiZap,
  FiUsers,
  FiCheckCircle,
  FiAlertTriangle,
  FiMail,
  FiShield,
  FiShare2,
  FiArrowLeft,
  FiArrowRight,
  FiCpu,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { getLastLoginInfo } from "../../utils/lastLoginInfo";
import { usePageTitle } from "../../hooks/usePageTitle";
import { parseExtensionAuthParams } from "../../utils/extensionAuthParams";
import { isClaudeGrant, isChatGptGrant } from "../../utils/mcpClients";
import cloudApi from "../../services/cloudApi";
// The same marks the connect funnel shows, from one source so the two surfaces
// can never disagree about which agents work.
import { MCP_AGENT_TILES } from "../ConnectClaude/connectClients";

// Dev-only password sign-in (a third button, under a "local dev only" divider).
// `process.env.NODE_ENV` is a compile-time constant here (webpack DefinePlugin),
// so production builds drop this whole branch, lazy import included — the
// component never ships. Real auth code is untouched: it just calls the existing
// password route. See DevPasswordSignIn.jsx; its prefill variables live in webpack.config.js.
const DevPasswordSignIn =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import(/* webpackChunkName: "dev-password-signin" */ "./DevPasswordSignIn"))
    : null;

const SignIn = () => {
  const { sendMagicLink, completeLoginCode, loginWithGoogle, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastLoginInfo, setLastLoginInfo] = useState(null);
  const [extensionLinkingStatus, setExtensionLinkingStatus] = useState(null);
  const [authStatus, setAuthStatus] = useState(null);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState("");
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRefs = useRef([]);
  const codeClientRef = useRef(null);
  const googleClientId = process.env.GOOGLE_OAUTH_CLIENT_ID;
  const googleRedirectUri =
    process.env.GOOGLE_OAUTH_REDIRECT_URI ||
    (typeof window !== "undefined" ? `${window.location.origin}/auth/google/callback` : undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const extensionAuthOptions = useMemo(() => {
    return parseExtensionAuthParams(location.search);
  }, [location.search]);
  const isExtensionAuthFlow = Boolean(extensionAuthOptions);

  // Optional post-login destination (e.g. the OAuth consent page sends users here
  // with returnTo=/oauth/consent?...). Only same-origin relative paths are honored —
  // anything else falls back to the dashboard so this can never open-redirect.
  const returnTo = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("returnTo");
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
    return "/dashboard";
  }, [location.search]);

  // An AI agent mid-handshake: /oauth/consent bounces here when the user isn't
  // signed in yet, so name what's happening instead of showing a bare form.
  const isAgentAuthFlow = returnTo.startsWith("/oauth/consent");

  // Name the agent when we recognize it. Only hosts we know (Claude, ChatGPT)
  // are ever named — the consent query is unvalidated at this point, so an
  // arbitrary host string never reaches our copy; unknown agents get the
  // generic banner. Preview without a real OAuth flow:
  //   /signin?returnTo=%2Foauth%2Fconsent%3Fclient_id%3Dhttps%253A%252F%252Fclaude.ai%252Fclient
  const consentAppName = useMemo(() => {
    if (!isAgentAuthFlow) return null;
    try {
      const query = new URLSearchParams(returnTo.split("?")[1] || "");
      const grant = { clientId: query.get("client_id") };
      if (isClaudeGrant(grant)) return "Claude";
      if (isChatGptGrant(grant)) return "ChatGPT";
    } catch {
      // Unparseable query — fall through to the generic banner.
    }
    return null;
  }, [isAgentAuthFlow, returnTo]);

  // Set page title
  usePageTitle("Sign In");

  // Load last login info on mount
  useEffect(() => {
    const info = getLastLoginInfo();
    if (info) {
      setLastLoginInfo(info);
      // Pre-fill email if last login was via email
      if (info.method === 'email' && info.email) {
        setEmail(info.email);
      }
    }
  }, []);

  // Check auth system status on mount
  useEffect(() => {
    cloudApi.request('/auth/status').then(setAuthStatus).catch(() => {});
  }, []);

  // Tick down the resend cooldown once per second
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Redirect to dashboard if already authenticated,
  // but first handle extension auth handoff if needed
  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (isExtensionAuthFlow) {
        setExtensionLinkingStatus("linking");
        cloudApi
          .maybePrepareExtensionSessionCookie(extensionAuthOptions)
          .then(() => navigate("/dashboard"))
          .catch(() => setExtensionLinkingStatus("error"));
      } else {
        navigate(returnTo);
      }
    }
  }, [isAuthenticated, loading, navigate, isExtensionAuthFlow, extensionAuthOptions, returnTo]);

  useEffect(() => {
    if (!googleClientId || typeof window === "undefined") return;

    let cancelled = false;
    const scriptId = "google-identity-services";
    let script = document.getElementById(scriptId);

    const initializeClient = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.oauth2) {
        setError((prev) => prev || "Google sign-in is unavailable right now.");
        return;
      }

      codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: googleClientId,
        scope: "openid email profile",
        ux_mode: "popup",
        redirect_uri: googleRedirectUri,
        callback: async (response) => {
          if (response.error) {
            setError("Google sign-in was cancelled. Please try again.");
            setGoogleLoading(false);
            return;
          }

          if (!response.code) {
            setError("Google did not return an authorization code.");
            setGoogleLoading(false);
            return;
          }

          try {
            await loginWithGoogle(response.code, extensionAuthOptions || undefined);
          } catch (err) {
            setError(err.message || "Google sign-in failed. Please try again.");
          } finally {
            setGoogleLoading(false);
          }
        },
      });
      setGoogleReady(true);
    };

    if (window.google?.accounts?.oauth2) {
      initializeClient();
      return;
    }

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        script.dataset.loaded = "true";
        initializeClient();
      };
      script.onerror = () => {
        setError("Unable to load Google sign-in. Please check your connection.");
    };
      document.body.appendChild(script);
    } else if (script.dataset.loaded === "true") {
      initializeClient();
    } else {
      script.addEventListener("load", initializeClient, { once: true });
    }

    return () => {
      cancelled = true;
      script?.removeEventListener?.("load", initializeClient);
    };
  }, [googleClientId, googleRedirectUri, loginWithGoogle, extensionAuthOptions]);

  const getFriendlyMagicLinkError = (message = "") => {
    const normalized = message.toLowerCase();
    if (normalized.includes("temporarily paused") || normalized.includes("temporarily unavailable")) {
      return "Service is temporarily unavailable. Please try again shortly.";
    }
    if (normalized.includes("not allowed") || normalized.includes("email_blocked")) {
      return "This email address is not allowed. Please use a different email.";
    }
    if (normalized.includes("too many verification requests") || normalized.includes("too many verification attempts")) {
      return "We've received a lot of requests. Please wait a couple of minutes and try again.";
    }
    return message || "An error occurred. Please try again.";
  };

  const handleGoogleSignIn = () => {
    setError("");
    setSuccess("");

    if (!googleClientId) {
      setError("Google sign-in is not configured.");
      return;
    }

    if (!googleReady || !codeClientRef.current) {
      setError("Google sign-in is still loading. Please try again in a moment.");
      return;
    }

    setGoogleLoading(true);
    codeClientRef.current.requestCode();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError("");
    setSuccess("");

    try {
      const emailToSend = email;
      await sendMagicLink(email, extensionAuthOptions || undefined);
      setSentEmail(emailToSend);
      setCodeDigits(["", "", "", "", "", ""]);
      setCodeError("");
      setEmail("");
      setSuccess(emailToSend);
      setResendCooldown(30);
    } catch (err) {
      setError(getFriendlyMagicLinkError(err.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-[#FFE100]"></div>
      </div>
    );
  }

  // Show maintenance page when auth is fully paused
  if (authStatus?.auth_paused) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black flex items-center justify-center p-4">
        <div className="max-w-md text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
            <FiAlertTriangle className="w-8 h-8 text-yellow-400" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-zinc-100">Service Maintenance</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400 leading-relaxed">
            {authStatus.auth_message || 'Service temporarily under maintenance. Please try again later.'}
          </p>
          <button
            onClick={() => cloudApi.request('/auth/status').then(setAuthStatus).catch(() => {})}
            className="mt-4 px-5 py-2 text-sm font-medium text-gray-700 dark:text-zinc-300 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded-lg border border-gray-200 dark:border-zinc-700 transition-colors"
          >
            Check again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black text-gray-900 dark:text-white">
        {/* Ambient background gradients */}
        <div
          className="absolute -left-24 -top-24 w-80 h-80 sm:w-96 sm:h-96 bg-blue-500/5 dark:bg-blue-500/10 blur-3xl rounded-full animate-pulse"
          aria-hidden="true"
          style={{ animationDuration: "8s" }}
        ></div>
        <div
          className="absolute -right-24 -bottom-24 w-80 h-80 sm:w-96 sm:h-96 bg-cyan-500/5 dark:bg-cyan-500/10 blur-3xl rounded-full animate-pulse"
          aria-hidden="true"
          style={{ animationDuration: "10s", animationDelay: "1s" }}
        ></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 min-h-screen flex flex-col">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center max-w-6xl mx-auto flex-1 relative">
            {/* Elegant vertical divider - only visible on large screens */}
            <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[70%]" aria-hidden="true">
              {/* Gradient line with glow effect */}
              <div className="relative h-full w-px">
                {/* Main gradient line */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/30 dark:via-blue-400/20 to-transparent"></div>
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/50 dark:via-blue-400/40 to-transparent blur-sm"></div>
                {/* Accent dots */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-500/60 dark:bg-blue-400/60 shadow-lg shadow-blue-500/50"></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-500/60 dark:bg-cyan-400/60 shadow-lg shadow-cyan-500/50"></div>
              </div>
            </div>

            {/* Auth form - focused and minimal */}
            <div className="order-1 lg:order-1 w-full max-w-[420px] mx-auto lg:mx-0">
              {/* Integrated header with form */}
              <div className="mb-6 sm:mb-8">
                {/* Logo and branding with elegant glow */}
                <div className="relative inline-flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 group">
                  <div className="relative">
                    {/* Animated gradient glow - multi-color */}
                    <div
                      className="absolute -inset-4 bg-gradient-to-br from-blue-500/30 via-cyan-500/20 to-purple-500/30 dark:from-blue-500/40 dark:via-cyan-500/30 dark:to-purple-500/40 rounded-3xl blur-2xl opacity-60 group-hover:opacity-100 transition-all duration-700 animate-pulse"
                      style={{ animationDuration: "3s" }}
                    ></div>
                    {/* Secondary rotating glow */}
                    <div
                      className="absolute -inset-3 bg-gradient-to-tr from-cyan-400/10 via-blue-400/10 to-purple-400/10 dark:from-cyan-400/20 dark:via-blue-400/20 dark:to-purple-400/20 rounded-2xl blur-xl group-hover:animate-spin"
                      style={{ animationDuration: "8s" }}
                    ></div>
                    {/* Logo container with gradient border */}
                    <div className="relative w-10 h-10 sm:w-14 sm:h-14 rounded-xl p-[2px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-2xl shadow-cyan-500/30 transform group-hover:scale-110 group-hover:rotate-[-3deg] transition-all duration-500">
                      {/* Inner white background */}
                      <div className="w-full h-full bg-white rounded-[10px] p-1.5 sm:p-2 flex items-center justify-center">
                        <img src="/logo-128.png" alt="Ultimate Web Scraper logo" className="w-full h-full" />
                      </div>
                    </div>
                    {/* Accent dots around logo */}
                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50 animate-pulse"></div>
                    <div className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50 animate-pulse" style={{ animationDelay: "0.5s" }}></div>
                  </div>

                  {/* Brand text with elegant typography */}
                  <div className="space-y-0.5 sm:space-y-1">
                    <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] font-semibold text-gray-500 dark:text-gray-400 leading-tight">Ultimate Web Scraper</p>
                    <div className="flex items-center gap-2 sm:gap-2.5">
                      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 dark:from-blue-400 dark:via-cyan-400 dark:to-purple-400 bg-clip-text text-transparent tracking-tight leading-none">
                        Cloud Platform
                      </h1>
                    </div>
                  </div>
                </div>

                {/* Sign in heading with refined spacing */}
                <div className="space-y-1.5 sm:space-y-2">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.1] tracking-tight text-gray-700 dark:text-zinc-200">
                    <span>Sign in</span>
                    <span className="mx-2 sm:mx-2.5 select-none font-thin text-gray-400 dark:text-zinc-500">/</span>
                    <span>Create account</span>
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-zinc-400 leading-relaxed max-w-md">Enter your email to get started or continue with Google</p>
                </div>

                {isExtensionAuthFlow && (
                  <div className="mt-4 rounded-xl border border-cyan-400/30 bg-cyan-500/[0.08] px-4 py-3 text-xs sm:text-sm text-cyan-900 dark:text-cyan-200">
                    You are connecting your Chrome extension. Complete sign in here, then return to the extension tab.
                  </div>
                )}

                {isAgentAuthFlow && !isExtensionAuthFlow && (
                  <div className="mt-4 rounded-xl border border-purple-400/30 bg-purple-500/[0.08] px-4 py-3 text-xs sm:text-sm text-purple-900 dark:text-purple-200">
                    <span className="font-semibold">
                      {consentAppName ? `Connecting ${consentAppName} to your workspace.` : "You are connecting an AI agent."}
                    </span>{" "}
                    Sign in or create your free account, then pick the workspace and access level it gets —
                    you&rsquo;ll go straight back to finish.
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-4" aria-label="Sign in form">
                {authStatus?.signup_paused && (
                  <div className="relative bg-yellow-500/5 dark:bg-yellow-500/[0.03] border border-yellow-500/20 text-yellow-700 dark:text-yellow-200 px-4 py-3 sm:px-5 sm:py-4 rounded-xl sm:rounded-2xl text-xs sm:text-sm backdrop-blur-xl">
                    <p className="leading-relaxed">{authStatus.signup_message || 'New account registration is temporarily paused. Existing users can still sign in.'}</p>
                  </div>
                )}

                {error && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="relative bg-red-500/5 dark:bg-red-500/[0.03] border border-red-500/20 text-red-600 dark:text-red-200 px-4 py-3 sm:px-5 sm:py-4 rounded-xl sm:rounded-2xl text-xs sm:text-sm backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-300"
                  >
                    <p className="leading-relaxed">{error}</p>
                  </div>
                )}

                <div className="relative group">
                  <label htmlFor="email-input" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitLoading || googleLoading}
                    className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl text-gray-900 dark:text-zinc-100 text-sm sm:text-base border border-gray-300/40 dark:border-gray-700/40 rounded-xl focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 focus:bg-white/80 dark:focus:bg-gray-800/80 focus-visible:outline-none placeholder-gray-400 dark:placeholder-zinc-500 transition-all duration-300 hover:border-gray-400/50 dark:hover:border-gray-600/50 hover:bg-white/70 dark:hover:bg-gray-800/70 disabled:opacity-50 disabled:cursor-not-allowed shadow-inner font-mono tracking-wide"
                    placeholder="you@company.com"
                    aria-describedby={error ? "email-error" : undefined}
                    aria-invalid={error ? "true" : "false"}
                  />
                  <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5 dark:ring-white/5 pointer-events-none"></div>
                </div>

                <button
                  type="submit"
                  disabled={submitLoading || googleLoading}
                  className="relative group w-full py-2.5 sm:py-3 px-4 sm:px-5 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 text-black font-bold text-xs sm:text-sm rounded-xl hover:shadow-2xl hover:shadow-blue-400/30 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 shadow-xl border border-blue-300/50 active:scale-[0.98] overflow-hidden"
                  aria-label={submitLoading ? "Sending magic link" : "Continue with email"}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  <span className="relative flex items-center justify-center gap-2 uppercase tracking-wide">
                    {submitLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <FiMail className="w-4 h-4" aria-hidden="true" />
                        <span>email me login code</span>
                      </>
                    )}
                  </span>
                </button>
              </form>

              <div className="mt-5 sm:mt-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex-1 border-t border-gray-300/40 dark:border-gray-700/40" aria-hidden="true"></div>
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-zinc-500 font-medium tracking-wide">or</span>
                  <div className="flex-1 border-t border-gray-300/40 dark:border-gray-700/40" aria-hidden="true"></div>
                </div>

                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading || submitLoading || !googleReady || !googleClientId}
                    className="relative group w-full flex items-center justify-center gap-2 sm:gap-2.5 px-3.5 py-2.5 sm:px-4 sm:py-3 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 hover:scale-[1.02] hover:shadow-xl hover:shadow-gray-500/10 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black overflow-hidden"
                    aria-label={googleLoading ? "Signing in with Google" : "Continue with Google"}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100/50 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    {googleLoading ? (
                      <svg className="relative animate-spin h-4 w-4 sm:h-5 sm:w-5 text-gray-900" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <FcGoogle className="relative w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
                    )}
                    <span className="relative text-xs sm:text-sm font-semibold text-gray-900">{googleLoading ? "Connecting..." : "Continue with Google"}</span>
                  </button>
                </div>
              </div>

              {DevPasswordSignIn && (
                <Suspense fallback={null}>
                  <DevPasswordSignIn
                    initialEmail={email}
                    extensionAuthOptions={extensionAuthOptions}
                    disabled={submitLoading || googleLoading}
                  />
                </Suspense>
              )}

              {lastLoginInfo && (
                <div className="mt-5 sm:mt-6 pt-4 sm:pt-5 border-t border-gray-300/40 dark:border-gray-700/40">
                  <p className="text-[11px] sm:text-xs text-gray-500 dark:text-zinc-400 text-center flex items-center justify-center gap-1.5 sm:gap-2">
                    <FiCheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-500/60 dark:text-cyan-400/60 flex-shrink-0" aria-hidden="true" />
                    <span>
                      Last signed in with {lastLoginInfo.method === "google" ? "Google" : "Email"}
                      {lastLoginInfo.email && <span className="text-gray-400 dark:text-zinc-500"> · {lastLoginInfo.email}</span>}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Platform Features - Elegant and minimal */}
            <aside className="order-2 lg:order-2" aria-label="Platform features">
              {/* Refined header */}
              <div className="mb-8 sm:mb-10 lg:mb-12">
                <div className="inline-flex items-center gap-1.5 mb-4 sm:mb-5">
                  <div className="w-1 h-1 rounded-full bg-blue-500/60 dark:bg-blue-400/60"></div>
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-blue-500/60 dark:text-blue-400/60 font-medium">CLOUD PLATFORM</p>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-extralight text-gray-800 dark:text-zinc-100 leading-[1.3] tracking-tight">
                  Web scraping for
                  <br />
                  <span className="font-normal text-gray-600 dark:text-zinc-300">people and AI agents</span>
                </h3>

                {/* Proof of the second half of that line, in the marks people already recognise */}
                <div className="mt-4 sm:mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex -space-x-1.5">
                    {MCP_AGENT_TILES.map(({ name, Mark }) => (
                      <div
                        key={name}
                        title={name}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white dark:bg-gray-900 ring-1 ring-black/[0.06] dark:ring-white/10 shadow-sm flex items-center justify-center"
                      >
                        <Mark className="w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem]" />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] sm:text-xs text-gray-500 dark:text-zinc-400">
                    Works with Claude, ChatGPT, Cursor &amp; any MCP agent
                    <Link
                      to="/connect"
                      className="ml-1.5 inline-flex items-center gap-1 text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                    >
                      see how
                      <FiArrowRight className="w-3 h-3" aria-hidden="true" />
                    </Link>
                  </p>
                </div>
              </div>

              {/* Scrollable feature list with fade at bottom */}
              <div className="relative">
                {/* Bottom fade - covers lower portion where 4th item peeks */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-100 dark:from-gray-950 via-gray-100/40 dark:via-gray-950/40 to-transparent z-10 pointer-events-none"
                  aria-hidden="true"
                ></div>

                <div
                  className="overflow-y-auto space-y-1 scrollbar-hide pb-24"
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    maxHeight: "330px", // 3 full items + visible portion of 4th
                  }}
                >
                  {[
                    {
                      icon: FiCpu,
                      title: "Connect your AI agents",
                      desc: "Scrape and run automations by just asking — no code, no copy-paste",
                      bg: "from-purple-500/[0.08] via-purple-500/[0.04] dark:from-purple-500/12 dark:via-purple-500/8",
                      bar: "via-purple-500/40 dark:via-purple-400/30",
                      glow: "bg-purple-400/30",
                      circle: "bg-purple-50 dark:bg-purple-500/15 border-purple-400/30 dark:border-purple-400/30 group-hover:border-purple-500/50 dark:group-hover:border-purple-400/50",
                      iconColor: "text-purple-500 dark:text-purple-400 group-hover:text-purple-600 dark:group-hover:text-purple-300",
                    },
                    {
                      icon: FiClock,
                      title: "Scheduled tasks",
                      desc: "Run your extractions in the cloud — hourly, daily, or weekly",
                      bg: "from-blue-500/[0.08] via-blue-500/[0.04] dark:from-blue-500/12 dark:via-blue-500/8",
                      bar: "via-blue-500/40 dark:via-blue-400/30",
                      glow: "bg-blue-400/30",
                      circle: "bg-blue-50 dark:bg-blue-500/15 border-blue-400/30 dark:border-blue-400/30 group-hover:border-blue-500/50 dark:group-hover:border-blue-400/50",
                      iconColor: "text-blue-500 dark:text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300",
                    },
                    {
                      icon: FiShield,
                      title: "Smart unblocking",
                      desc: "Automatic captcha solving and proxies, built in",
                      bg: "from-cyan-500/[0.08] via-cyan-500/[0.04] dark:from-cyan-500/12 dark:via-cyan-500/8",
                      bar: "via-cyan-500/40 dark:via-cyan-400/30",
                      glow: "bg-cyan-400/30",
                      circle: "bg-cyan-50 dark:bg-cyan-500/15 border-cyan-400/30 dark:border-cyan-400/30 group-hover:border-cyan-500/50 dark:group-hover:border-cyan-400/50",
                      iconColor: "text-cyan-500 dark:text-cyan-400 group-hover:text-cyan-600 dark:group-hover:text-cyan-300",
                    },
                    {
                      icon: FiShare2,
                      title: "Integrations",
                      desc: "Send your data to webhooks, n8n, Make, and more",
                      bg: "from-purple-500/[0.08] via-purple-500/[0.04] dark:from-purple-500/12 dark:via-purple-500/8",
                      bar: "via-purple-500/40 dark:via-purple-400/30",
                      glow: "bg-purple-400/30",
                      circle: "bg-purple-50 dark:bg-purple-500/15 border-purple-400/30 dark:border-purple-400/30 group-hover:border-purple-500/50 dark:group-hover:border-purple-400/50",
                      iconColor: "text-purple-500 dark:text-purple-400 group-hover:text-purple-600 dark:group-hover:text-purple-300",
                    },
                    {
                      icon: FiZap,
                      title: "Parallel execution",
                      desc: "Run multiple extractions simultaneously with our cloud browsers",
                      bg: "from-blue-500/[0.08] via-blue-500/[0.04] dark:from-blue-500/12 dark:via-blue-500/8",
                      bar: "via-blue-500/40 dark:via-blue-400/30",
                      glow: "bg-blue-400/30",
                      circle: "bg-blue-50 dark:bg-blue-500/15 border-blue-400/30 dark:border-blue-400/30 group-hover:border-blue-500/50 dark:group-hover:border-blue-400/50",
                      iconColor: "text-blue-500 dark:text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300",
                    },
                    {
                      icon: FiUsers,
                      title: "Team collaboration",
                      desc: "Share access securely with your team without credential exchanges",
                      bg: "from-cyan-500/[0.08] via-cyan-500/[0.04] dark:from-cyan-500/12 dark:via-cyan-500/8",
                      bar: "via-cyan-500/40 dark:via-cyan-400/30",
                      glow: "bg-cyan-400/30",
                      circle: "bg-cyan-50 dark:bg-cyan-500/15 border-cyan-400/30 dark:border-cyan-400/30 group-hover:border-cyan-500/50 dark:group-hover:border-cyan-400/50",
                      iconColor: "text-cyan-500 dark:text-cyan-400 group-hover:text-cyan-600 dark:group-hover:text-cyan-300",
                    },
                  ].map(({ icon: Icon, title, desc, bg, bar, glow, circle, iconColor }) => (
                    <div key={title} className="group relative py-4 sm:py-5 rounded-xl transition-all duration-300">
                      <div className={`absolute inset-0 bg-gradient-to-r ${bg} to-transparent rounded-xl`}></div>
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent ${bar} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                      ></div>
                      <div className="relative flex items-start gap-3 sm:gap-4 pl-4 transition-all duration-300 group-hover:pl-5">
                        <div className="relative mt-0.5">
                          <div className={`absolute inset-0 ${glow} blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                          <div className={`relative w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-300 ${circle}`}>
                            <Icon className={`w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] transition-colors duration-300 ${iconColor}`} aria-hidden="true" />
                          </div>
                        </div>
                        <div className="space-y-1 pt-0.5">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-zinc-100 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300">
                            {title}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-zinc-400 leading-relaxed group-hover:text-gray-700 dark:group-hover:text-zinc-300 transition-colors duration-300">
                            {desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Extension linking overlay — shown when user is already authenticated */}
      {extensionLinkingStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md"></div>

          <div className="relative w-full max-w-sm">
            <div className="relative overflow-hidden bg-gradient-to-b from-gray-900/95 to-gray-950/95 backdrop-blur-xl rounded-2xl border border-zinc-700/40 shadow-2xl">
              <div className="relative px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex flex-col items-center text-center">
                  {extensionLinkingStatus === "linking" && (
                    <>
                      <div className="w-12 h-12 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-5">
                        <svg className="w-5 h-5 text-blue-400 animate-spin" style={{ animationDuration: "1s" }} viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-semibold text-zinc-100">Connecting extension...</h3>
                      <p className="text-sm text-zinc-400 mt-2">Just a moment.</p>
                    </>
                  )}

                  {extensionLinkingStatus === "error" && (
                    <>
                      <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-5">
                        <FiAlertTriangle className="w-6 h-6 text-red-400" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-semibold text-zinc-100">Something went wrong</h3>
                      <p className="text-sm text-zinc-400 mt-2">Please try again from the extension.</p>
                    </>
                  )}

                  <button
                    onClick={() => navigate("/dashboard")}
                    className="mt-6 px-5 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-medium rounded-lg border border-zinc-700 transition-colors"
                  >
                    Go to dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Code Entry Dialog */}
      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md animate-in fade-in duration-200"></div>

          {/* Dialog */}
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            {/* Outer glow effect */}
            <div
              className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-75 animate-pulse"
              style={{ animationDuration: "3s" }}
            ></div>

            <div className="relative overflow-hidden bg-gradient-to-b from-gray-900/95 to-gray-950/95 backdrop-blur-xl rounded-2xl border border-blue-500/20 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
              {/* Subtle inner gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5"></div>

              {/* Top accent line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-blue-400/60 to-transparent"></div>

              {/* Content */}
              <div className="relative px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex flex-col items-center text-center">
                  {/* Header */}
                  <div className="space-y-2 mb-6">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-tight bg-gradient-to-r from-blue-300 via-cyan-400 to-blue-400 bg-clip-text text-transparent">Enter login code</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed max-w-xs">
                      We sent a 6-digit code to <span className="text-zinc-300 font-medium">{success}</span>
                    </p>
                    {isExtensionAuthFlow && <p className="text-xs text-zinc-500 mt-1">After signing in, return to your extension tab.</p>}
                    {isAgentAuthFlow && <p className="text-xs text-zinc-500 mt-1">After signing in, you&rsquo;ll authorize your agent&rsquo;s access.</p>}
                  </div>

                  {/* Code error */}
                  {codeError && (
                    <div className="w-full mb-4 bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-2.5 rounded-xl text-xs sm:text-sm animate-in fade-in slide-in-from-top-2 duration-200">
                      {codeError}
                    </div>
                  )}

                  {/* 6-digit code inputs */}
                  <div className="flex gap-2 sm:gap-3 mb-6">
                    {codeDigits.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (codeInputRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        value={digit}
                        disabled={codeVerifying}
                        autoFocus={idx === 0}
                        className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold bg-zinc-800/80 border border-zinc-600/40 rounded-lg text-zinc-100 focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 focus-visible:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (!val && !digit) return;
                          const newDigits = [...codeDigits];
                          newDigits[idx] = val.slice(-1);
                          setCodeDigits(newDigits);
                          setCodeError("");
                          // Auto-advance
                          if (val && idx < 5) {
                            codeInputRefs.current[idx + 1]?.focus();
                          }
                        }}
                        onKeyDown={(e) => {
                          // Backspace: clear current then move back
                          if (e.key === "Backspace" && !digit && idx > 0) {
                            const newDigits = [...codeDigits];
                            newDigits[idx - 1] = "";
                            setCodeDigits(newDigits);
                            codeInputRefs.current[idx - 1]?.focus();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                          if (!pasted) return;
                          const newDigits = [...codeDigits];
                          for (let i = 0; i < 6; i++) {
                            newDigits[i] = pasted[i] || "";
                          }
                          setCodeDigits(newDigits);
                          setCodeError("");
                          // Focus last filled or the next empty
                          const focusIdx = Math.min(pasted.length, 5);
                          codeInputRefs.current[focusIdx]?.focus();
                        }}
                        onFocus={(e) => e.target.select()}
                      />
                    ))}
                  </div>

                  {/* Verify button */}
                  <button
                    onClick={async () => {
                      const code = codeDigits.join("");
                      if (code.length !== 6) {
                        setCodeError("Please enter all 6 digits");
                        return;
                      }
                      setCodeVerifying(true);
                      setCodeError("");
                      try {
                        await completeLoginCode(sentEmail, code, extensionAuthOptions || undefined);
                      } catch (err) {
                        setCodeError(getFriendlyMagicLinkError(err.message));
                        setCodeDigits(["", "", "", "", "", ""]);
                        setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
                      } finally {
                        setCodeVerifying(false);
                      }
                    }}
                    disabled={codeVerifying || codeDigits.join("").length !== 6}
                    className="w-full py-2.5 sm:py-3 px-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 text-black font-bold text-xs sm:text-sm rounded-xl hover:shadow-xl hover:shadow-blue-400/20 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 active:scale-[0.98] border border-blue-300/50 mb-4"
                  >
                    {codeVerifying ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </span>
                    ) : (
                      "Verify code"
                    )}
                  </button>

                  {/* Resend + back */}
                  <div className="flex items-center gap-3 text-xs">
                    <button
                      onClick={() => {
                        setSuccess("");
                        setSentEmail("");
                        setCodeDigits(["", "", "", "", "", ""]);
                        setCodeError("");
                      }}
                      className="text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1"
                    >
                      <FiArrowLeft className="w-3 h-3" />
                      Back
                    </button>
                    <span className="text-zinc-700">|</span>
                    <button
                      onClick={async () => {
                        if (resendLoading || resendCooldown > 0) return;
                        setResendLoading(true);
                        setCodeError("");
                        setCodeDigits(["", "", "", "", "", ""]);
                        try {
                          await sendMagicLink(sentEmail, extensionAuthOptions || undefined);
                          setResendCooldown(30);
                        } catch (err) {
                          setCodeError(getFriendlyMagicLinkError(err.message));
                        } finally {
                          setResendLoading(false);
                        }
                        setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
                      }}
                      disabled={resendLoading || resendCooldown > 0}
                      className="text-blue-400/80 hover:text-blue-300 transition-colors disabled:text-zinc-600 disabled:cursor-not-allowed disabled:hover:text-zinc-600"
                    >
                      {resendLoading
                        ? "Sending..."
                        : resendCooldown > 0
                          ? `Resend in ${resendCooldown}s`
                          : "Resend code"}
                    </button>
                  </div>

                  {/* Spam folder hint */}
                  <p className="text-[11px] text-zinc-500 mt-4">
                    Didn't receive it? Check your <span className="text-red-400/90 font-medium">spam folder</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignIn;
